/**
 * Race Channel Manager
 *
 * Encapsulates Supabase Realtime channel management for race hosting.
 * Handles both the presence/broadcast channel and the database changes channel.
 */

import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { RaceParticipant } from '@/types/race';

export interface ChannelCallbacks {
  /** Called when participants list changes (presence events) */
  onParticipantsSync?: () => void;
  /** Called when live_stats table changes */
  onLiveStatsChange?: (userId: string, cardsCompleted: number, correctCount: number) => void;
  /** Called when a participant finishes */
  onParticipantFinish?: (participant: RaceParticipant) => void;
  /** Called on channel errors */
  onError?: (error: string) => void;
  /** Called when connection status changes */
  onConnectionChange?: (connected: boolean) => void;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

/**
 * Manages Supabase Realtime channels for race hosting.
 * Provides a unified interface for presence tracking and database change subscriptions.
 */
export class RaceChannelManager {
  private raceRoomId: string;
  private userId: string;
  private presenceChannel: RealtimeChannel | null = null;
  private dbChannel: RealtimeChannel | null = null;
  private callbacks: ChannelCallbacks;
  private connectionStatus: ConnectionStatus = 'disconnected';

  constructor(raceRoomId: string, userId: string, callbacks: ChannelCallbacks = {}) {
    this.raceRoomId = raceRoomId;
    this.userId = userId;
    this.callbacks = callbacks;
  }

  /**
   * Initialize both channels and subscribe.
   */
  async initialize(): Promise<{ success: boolean; error?: string }> {
    try {
      this.connectionStatus = 'connecting';

      await this.initializePresenceChannel();
      await this.initializeDatabaseChannel();

      this.connectionStatus = 'connected';
      this.callbacks.onConnectionChange?.(true);

      return { success: true };
    } catch (error) {
      this.connectionStatus = 'error';
      this.callbacks.onConnectionChange?.(false);
      const errorMessage = (error as Error).message;
      this.callbacks.onError?.(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Initialize the presence/broadcast channel for participant tracking.
   */
  private async initializePresenceChannel(): Promise<void> {
    this.presenceChannel = supabase.channel(`race:${this.raceRoomId}`, {
      config: {
        broadcast: { self: false },
        presence: { key: this.userId },
      },
    });

    // Track presence events
    this.presenceChannel.on('presence', { event: 'sync' }, () => {
      this.callbacks.onParticipantsSync?.();
    });

    this.presenceChannel.on('presence', { event: 'join' }, () => {
      this.callbacks.onParticipantsSync?.();
    });

    this.presenceChannel.on('presence', { event: 'leave' }, () => {
      this.callbacks.onParticipantsSync?.();
    });

    // Subscribe and announce host presence
    await this.presenceChannel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Host connected to race channel:', this.raceRoomId);
        await this.presenceChannel?.track({
          user_id: this.userId,
          username: 'Host',
          app_name: 'host',
          status: 'ready',
        });
      } else if (status === 'CHANNEL_ERROR') {
        console.error('Race channel error');
        this.connectionStatus = 'error';
        this.callbacks.onConnectionChange?.(false);
        this.callbacks.onError?.('Lost connection to race channel');
      } else if (status === 'CLOSED') {
        console.log('Race channel closed');
        this.connectionStatus = 'disconnected';
        this.callbacks.onConnectionChange?.(false);
      }
    });
  }

  /**
   * Initialize the database changes channel for live stats updates.
   */
  private async initializeDatabaseChannel(): Promise<void> {
    this.dbChannel = supabase
      .channel(`race-live-stats:${this.raceRoomId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'race',
          table: 'live_stats',
          filter: `race_room_id=eq.${this.raceRoomId}`,
        },
        (payload) => {
          const stats = payload.new as {
            user_id: string;
            cards_completed: number;
            correct_count: number;
          };
          this.callbacks.onLiveStatsChange?.(
            stats.user_id,
            stats.cards_completed,
            stats.correct_count
          );
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'race',
          table: 'participants',
          filter: `race_room_id=eq.${this.raceRoomId}`,
        },
        (payload) => {
          const participant = payload.new as RaceParticipant;
          if (participant.status === 'finished') {
            this.callbacks.onParticipantFinish?.(participant);
          }
          // Trigger sync for any participant update
          this.callbacks.onParticipantsSync?.();
        }
      );

    await this.dbChannel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log('Live stats channel connected');
      } else if (status === 'CHANNEL_ERROR' || status === 'CLOSED') {
        console.log('Live stats channel disconnected:', status);
      }
    });
  }

  /**
   * Send a broadcast message to all participants.
   */
  async broadcast<T extends object>(event: string, payload: T): Promise<void> {
    if (!this.presenceChannel) {
      throw new Error('Presence channel not initialized');
    }

    await this.presenceChannel.send({
      type: 'broadcast',
      event,
      payload,
    });
  }

  /**
   * Reconnect both channels (e.g., after tab visibility change).
   */
  async reconnect(): Promise<{ success: boolean; error?: string }> {
    console.log('🔄 Reconnecting race channels...');

    try {
      // Cleanup existing channels
      await this.cleanup();

      // Reinitialize
      return await this.initialize();
    } catch (error) {
      console.error('❌ Failed to reconnect:', error);
      return { success: false, error: (error as Error).message };
    }
  }

  /**
   * Check if channels are connected.
   */
  isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }

  /**
   * Get current connection status.
   */
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Update callbacks (useful when component state changes).
   */
  updateCallbacks(callbacks: Partial<ChannelCallbacks>): void {
    this.callbacks = { ...this.callbacks, ...callbacks };
  }

  /**
   * Clean up and unsubscribe from all channels.
   */
  async cleanup(): Promise<void> {
    if (this.presenceChannel) {
      await supabase.removeChannel(this.presenceChannel);
      this.presenceChannel = null;
    }
    if (this.dbChannel) {
      await supabase.removeChannel(this.dbChannel);
      this.dbChannel = null;
    }
    this.connectionStatus = 'disconnected';
  }
}
